import { usePARCBalance, usePARTBalance } from '@puzzlehq/frontend-utils';
import {
  ConnectResponse,
  PuzzleAccount,
  useAccount,
  useConnect,
} from '@puzzlehq/sdk';
import { useSettings } from '@src/data/settings';
import { trpcReact } from '@src/data/trpc';
import { useUserCountryCode } from '@src/hooks/useQuests';
import { TCountryCode } from 'countries-list';
import { createContext, useEffect, useMemo } from 'react';

export type QuestContextType = {
  partBalance: number | undefined;
  parcBalance: number | undefined;
  account: PuzzleAccount | undefined;
  isPuzzleWalletWebview: boolean | undefined;
  isPro: boolean | undefined;
  isConnected: boolean | undefined;
  connectLoading: boolean | undefined;
  connectError: unknown;
  connect: (() => Promise<ConnectResponse>) | undefined;
};

export const QuestContext = createContext<QuestContextType | null>(null);

export const QuestProvider = ({ children }: { children: React.ReactNode }) => {
  const { account } = useAccount();

  const { partBalance } = usePARTBalance();
  const { parcBalance } = usePARCBalance();

  const { connect, isConnected, loading, error } = useConnect({
    permissions: {
      programIds: {
        [import.meta.env.VITE_NETWORK!]: [
          import.meta.env.VITE_COIN_CONTRACT!,
          import.meta.env.VITE_TICKET_CONTRACT!,
        ],
      },
    },
    dAppInfo: {
      name: 'Puzzle Arcade Quests',
    },
  });

  const { data: isPro } = trpcReact.getIsPro.useQuery(
    { address: account?.address! },
    { enabled: !!account, staleTime: 60_000 }
  );

  const { isPuzzleWalletWebview } = useMemo(() => {
    const userAgent = window.navigator.userAgent;

    if (userAgent.includes('PuzzleWallet')) {
      return { isPuzzleWalletWebview: true };
    }
    return { isPuzzleWalletWebview: false };
  }, [window.navigator.userAgent]);

  const { data: defaultCountryCode } = useUserCountryCode();
  const [storedCountryCode, storeCountryCode] = useSettings((state) => [
    state.countryCode,
    state.setCountryCode,
  ]);

  useEffect(() => {
    if (storedCountryCode === undefined && defaultCountryCode !== undefined) {
      storeCountryCode(defaultCountryCode as TCountryCode);
    }
  }, [storedCountryCode, defaultCountryCode]);

  return (
    <QuestContext.Provider
      value={{
        account,
        partBalance: partBalance,
        connect,
        connectError: error,
        connectLoading: loading,
        isConnected,
        isPuzzleWalletWebview,
        isPro,
        parcBalance,
      }}
    >
      {children}
    </QuestContext.Provider>
  );
};
